import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  MenuOptionValue,
  NestedPick,
  Paginated,
  PaginationQuery,
  PaginationQueryWithFields,
  Version,
  Versions,
} from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import {
  CheckMileageBody,
  ConflictMileage,
  Mileage,
  MileageNoCarDependency,
  MileageReminderRecipients,
  MileageVersion,
  SendMileageReminderBody,
  SendMileageRemindersBody,
  SendMileageRemindersResult,
  UpsertMileage,
} from "./types";

export const useMyCarMileagesApi = () => {
  const api = useApi();

  const useCarMileages = useCallback(
    <K extends keyof MileageNoCarDependency>(
      query: PaginationQueryWithFields<NestedPick<MileageNoCarDependency, K>>,
      signal: AbortSignal,
      carId: string,
      carDriverId: string,
    ) => {
      const url = buildUrl<NestedPick<MileageNoCarDependency, K>>(`/my_cars/${carId}/${carDriverId}/mileages`, query);
      return useSWR<Paginated<NestedPick<MileageNoCarDependency, K>>, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<MileageNoCarDependency, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const insertMileage = useCallback(
    async (body: UpsertMileage, carDriverId: string) => {
      const url = `/my_cars/${body.carId}/${carDriverId}/mileages`;
      await api.post<UpsertMileage, { id: string }>(url, body);
      api.mutateCache([
        url,
        `/admin/mileages`,
        `/admin/cars/${body.carId}/mileages`,
        `/my_cars/${body.carId}/${carDriverId}/mileages`,
        // `/my_cars/${body.carId}/${carDriverId}`,
      ]);
    },
    [api],
  );

  const getConflictMileages = useCallback(
    async (formBody: CheckMileageBody, carId: string) => {
      const url = `/my_cars/${carId}/mileages/conflicts`;
      const result = await api.post<CheckMileageBody, ConflictMileage>(url, formBody);
      return result;
    },
    [api],
  );

  const getLastMileage = useCallback(
    (signal: AbortSignal, carId?: string, carDriverId?: string) => {
      const url = carId && carDriverId ? `/my_cars/${carId}/${carDriverId}/mileages/last` : "";
      return api.get<Mileage>({ url, signal });
    },
    [api],
  );

  const getMileage = useCallback(
    (signal: AbortSignal, mileageId: string, carId?: string, carDriverId?: string) => {
      const url = carId && carDriverId ? `/my_cars/${carId}/${carDriverId}/mileages/${mileageId}` : "";
      return useSWRMutation<Mileage | null, CustomError, string>(url, (url) => api.get<Mileage>({ url, signal }));
    },
    [api],
  );

  const updateMileage = useCallback(
    async (id: string, body: UpsertMileage, carId: string, carDriverId: string) => {
      const url = `/my_cars/${carId}/${carDriverId}/mileages/${id}`;
      await api.put<UpsertMileage>(url, body);
      api.mutateCache([
        url,
        `/admin/mileages`,
        `/admin/cars/${body.carId}/mileages`,
        `/my_cars/${carId}/${carDriverId}/mileages`,
        `/admin/mileages/${id}/versions`,
        `/my_cars/${carId}/${carDriverId}`,
      ]);
      carDriverId && api.mutateCache([`/cars/${body.carId}/${carDriverId}/mileages`]);
    },
    [api],
  );

  const deleteMileage = useCallback(
    async (mileageId: string, carId: string, carDriverId: string): Promise<void> => {
      const url = `/my_cars/${carId}/${carDriverId}/mileages/${mileageId}`;
      await api.del(url).then(() => {
        api.mutateCache([
          `/admin/mileages`,
          `/admin/cars/${carId}/mileages`,
          `/my_cars/${carId}/${carDriverId}/mileages`,
          `/admin/mileages/${mileageId}/versions`,
          `/my_cars/${carId}/${carDriverId}`,
        ]);
      });
    },
    [api],
  );

  return {
    useCarMileages,
    insertMileage,
    getLastMileage,
    updateMileage,
    deleteMileage,
    getMileage,
    getConflictMileages,
  };
};

export const useAdminMileagesApi = () => {
  const api = useApi();

  const getMileage = useCallback(
    (mileageId: string, signal: AbortSignal) => {
      const url = `/admin/mileages/${mileageId}`;
      return useSWRMutation<Mileage | null, CustomError, string>(url, (url) => api.get<Mileage>({ url, signal }));
    },
    [api],
  );

  const useCarMileages = useCallback(
    <K extends keyof MileageNoCarDependency>(
      query: PaginationQueryWithFields<NestedPick<MileageNoCarDependency, K>>,
      signal: AbortSignal,
      carId?: string,
    ) => {
      const url = buildUrl<NestedPick<MileageNoCarDependency, K>>(
        carId ? `/admin/cars/${carId}/mileages` : "/admin/mileages",
        query,
      );
      return useSWR<Paginated<NestedPick<MileageNoCarDependency, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<MileageNoCarDependency, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const insertMileage = useCallback(
    async (body: UpsertMileage) => {
      const url = `/admin/mileages`;
      await api.post<UpsertMileage, { id: string }>(url, body);
      api.mutateCache([url, `/admin/cars/${body.carId}/mileages`]);
    },
    [api],
  );

  const updateMileage = useCallback(
    async (id: string, body: UpsertMileage, carDriverId?: string) => {
      const url = `/admin/mileages/${id}`;
      await api.put<UpsertMileage>(url, body);
      api.mutateCache([`/admin/mileages`, `/admin/cars/${body.carId}/mileages`, `/admin/mileages/${id}/versions`]);
      carDriverId && api.mutateCache([`/cars/${body.carId}/${carDriverId}/mileages`]);
    },
    [api],
  );

  const deleteMileage = useCallback(
    async (mileageId: string, carId?: string): Promise<void> => {
      const url = `/admin/mileages/${mileageId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/mileages`, `/admin/cars/${carId}/mileages`]);
      });
    },
    [api],
  );

  const getCarMileages = useCallback(
    (query: PaginationQuery, signal: AbortSignal, carId?: string) => {
      const url = buildUrl(carId ? `/admin/cars/${carId}/mileages` : `/admin/mileages`, query);
      return api.get<Paginated<Mileage>>({ url, signal });
    },
    [api],
  );

  const getMileageHistory = useCallback(
    (query: PaginationQuery, mileageID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/mileages/${mileageID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getMileageVersion = useCallback(
    (mileageId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/mileages/${mileageId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<MileageVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<MileageVersion>>({ url, signal }),
      );
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/mileages/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  const getLastMileage = useCallback(
    (signal: AbortSignal, carId?: string) => {
      const url = carId ? `/admin/cars/${carId}/mileages/last` : "";
      return api.get<Mileage>({ url, signal });
    },
    [api],
  );

  const getConflictMileages = useCallback(
    async (formBody: CheckMileageBody) => {
      const url = `/admin/cars/${formBody.carId}/mileages/conflicts`;
      const result = await api.post<CheckMileageBody, ConflictMileage>(url, formBody);
      return result;
    },
    [api],
  );

  const sendMileageReminder = useCallback(
    async (carId: string, body: SendMileageReminderBody): Promise<void> => {
      const url = `/admin/cars/${carId}/mileages/reminder`;
      await api.postNoResult<SendMileageReminderBody>(url, body);
    },
    [api],
  );

  const useMileageReminderRecipients = useCallback(
    (signal: AbortSignal, onlyOverdue?: boolean, subsidiaryIds?: string[]) => {
      const params = new URLSearchParams();
      onlyOverdue !== undefined && params.set("onlyOverdue", `${onlyOverdue}`);
      subsidiaryIds?.forEach((id) => params.append("subsidiaryIds", id));

      // the recipients type has no default: nothing is loaded until it is chosen
      const key = onlyOverdue === undefined ? null : `/admin/mileages/reminder/recipients?${params.toString()}`;

      return useSWR<MileageReminderRecipients | null, CustomError, string | null>(key, (url: string) =>
        api.get<MileageReminderRecipients>({ url, signal }),
      );
    },
    [api],
  );

  const sendMileageReminders = useCallback(
    async (body: SendMileageRemindersBody) => {
      const url = `/admin/mileages/reminder`;
      return await api.post<SendMileageRemindersBody, SendMileageRemindersResult>(url, body);
    },
    [api],
  );

  return {
    deleteMileage,
    updateMileage,
    insertMileage,
    getMileage,
    useCarMileages,
    getCarMileages,
    getMileageHistory,
    getMileageVersion,
    getFieldFilters,
    getLastMileage,
    getAdminConflictMileages: getConflictMileages,
    sendMileageReminder,
    useMileageReminderRecipients,
    sendMileageReminders,
  };
};
